/**
 * Free shipping threshold display component
 * Shows dynamic messaging about free shipping progress based on cart total and product price
 */

import { render, createElement as h } from 'preact'
import { useEffect, useRef } from 'preact/hooks'
import useCartGlobal, { CartConfigKeys } from '../hooks/useCartGlobal'
import { Cart } from '../helpers/cart.types'
import { emitterProps } from '../helpers/emitter-event'

interface FreeShippingThreshold {
  min_threshold: number
  message: any
  threshold_message?: any
}

const extractTextContent = (node: any): string => {
  if (!node) return ''
  if (typeof node === 'string') return node
  if (Array.isArray(node)) return node.map(extractTextContent).join('')
  if (typeof node === 'object') {
    if (typeof node.value === 'string') return node.value
    if (Array.isArray(node.children)) return node.children.map(extractTextContent).join('')
  }
  return ''
}

const getThresholdMessage = (t: FreeShippingThreshold) => extractTextContent(t.threshold_message)

interface FreeShippingUSPComponentProps {
  productId: string
  productPriceMin: number
  freeShippingThresholds: FreeShippingThreshold[]
  textClass?: string
  element?: HTMLElement
}

function FreeShippingUSPComponent({
  productId,
  productPriceMin,
  freeShippingThresholds = [],
  textClass,
  element
}: FreeShippingUSPComponentProps) {
  const { cartConfig, handleSetCartConfig } = useCartGlobal()
  const cart = cartConfig.cart as Cart
  const lastMessageRef = useRef('')
  const hasInitializedRef = useRef(false)

  const updateMessage = () => {
    if (!productId || !cart || !hasInitializedRef.current) return

    const cartTotal = cart.total_price || 0
    const totalWithProduct = cartTotal + (productPriceMin || 0)
    const sorted = [...freeShippingThresholds].sort((a, b) => a.min_threshold - b.min_threshold)
    const reversed = [...sorted].reverse()

    const current = reversed.find((t) => cartTotal >= (t.min_threshold || 0))
    const next = reversed.find((t) => totalWithProduct >= (t.min_threshold || 0))

    let message = ''

    if (!current) {
      const lowest = sorted[0]
      const thresholdMsg = getThresholdMessage(lowest)
      message = thresholdMsg || extractTextContent(lowest.message)
    } else if (next && next.min_threshold !== current.min_threshold) {
      const thresholdMsg = getThresholdMessage(current)
      message = thresholdMsg || extractTextContent(current.message)
    } else {
      message = extractTextContent(current.message)
    }

    if (element && message && message !== lastMessageRef.current) {
      element.innerHTML = `<div class="${textClass || ''}">${message}</div>`
      lastMessageRef.current = message
    }
  }

  useEffect(() => {
    const handleCartUpdate = () => {
      if (window.Cart) {
        window.Cart.getCart().then(() => {
          if (window.Cart?.value) {
            handleSetCartConfig('cart' as keyof typeof CartConfigKeys, window.Cart.value)
            hasInitializedRef.current = true
            updateMessage()
          }
        })
      }
    }

    const events = [
      ...Object.values(emitterProps.cart),
      'quantity-changed',
      'cart:updated',
      'cart:changed'
    ]

    events.forEach((e) => document.addEventListener(e, handleCartUpdate))
    return () => events.forEach((e) => document.removeEventListener(e, handleCartUpdate))
  }, [cart?.total_price, cart?.item_count, productId, productPriceMin, freeShippingThresholds])

  return null
}

export class FreeShippingUSP extends HTMLElement {
  private productId = ''
  private productPriceMin = 0
  private thresholds: FreeShippingThreshold[] = []
  private textClass = ''

  connectedCallback() {
    this.parseAttributes()
    this.loadThresholds()
    this.renderComponent()
  }

  private parseAttributes() {
    this.productId = this.getAttribute('data-product-id') || ''
    this.productPriceMin = parseInt(this.getAttribute('data-product-price-min') || '0', 10)
    this.textClass = this.getAttribute('data-text-class') || ''
  }

  private loadThresholds() {
    try {
      const script = document.querySelector('[data-free-shipping-usp-data]')
      if (script) {
        this.thresholds = JSON.parse(script.textContent || '[]')
        return
      }
      if (window.globals?.metaobjects?.free_shipping_usp) {
        this.thresholds = window.globals.metaobjects.free_shipping_usp
        return
      }
      console.warn('Free shipping thresholds not found')
    } catch (err) {
      console.error('Error loading free shipping thresholds:', err)
    }
  }

  private renderComponent() {
    render(
      h(FreeShippingUSPComponent, {
        productId: this.productId,
        productPriceMin: this.productPriceMin,
        freeShippingThresholds: this.thresholds,
        textClass: this.textClass,
        element: this
      }),
      this
    )
  }
}
